/**
 * The speedrun note (docs/notes.md "The speedrun note", flag speedrunUI; owner, 2026-10-20: "Add a
 * speedrun note editable by the hue user or dev tools that allows setting a speedrun time and a
 * title", "It does not expire", "Better yet, it should pull the latest best times from mathue_ on
 * twitch"). One slip on the notice board that never comes down: Hue's most recently posted Paper
 * Mario: The Thousand-Year Door personal best from speedrun.com (his account there is linked to
 * the Twitch mathue_), or a title and a time Hue (or a steward, from Dev tools) set by hand until
 * "Back to live".
 *
 * Pure rules shared by the server and both clients: picking the PB, the clock's format and its
 * parse, the place's ordinal, who may change it, and what the board shows.
 */
import { sameName } from "./notes.ts";
import { GALLERY_PEON } from "./weekly.ts";
import { MENTION_INK, type MentionColour } from "./mentions.ts";

/** The peon whose note it is, matched ignoring case and the ends' spaces (as names are everywhere). */
export const SPEEDRUN_RUNNER = "Hue";
/** His Twitch login; speedrun.com looks the account up by it (`/users?twitch=`). */
export const SPEEDRUN_TWITCH = "mathue_";
/**
 * The one game the live note follows: Paper Mario: The Thousand-Year Door on speedrun.com (`ttyd`),
 * not its Category Extensions board (`ttydextras`, lde3rnl6) and no other game (owner, 2026-10-23:
 * "let's just stick to Paper Mario", "I don't want it to do most recently of any other game").
 */
export const SPEEDRUN_GAME_ID = "m1zjo360";
/** A hand-set title's longest, in code points. */
export const SPEEDRUN_TITLE_MAX = 60;
/** A hand-set time's longest: 99:59:59.99. */
export const SPEEDRUN_TIME_MAX_S = 100 * 3600 - 0.01;
/** How long a fetched PB is good for before the next read refreshes it behind the answer. */
export const SPEEDRUN_REFRESH_MS = 30 * 60 * 1000;

/** One personal best as the board needs it, picked out of speedrun.com's answer. */
export interface SpeedrunPb {
  game: string;
  category: string;
  /** Seconds, as speedrun.com's `primary_t` (fractions only when the run has them). */
  time_s: number;
  place: number | null;
  /** The run's day, YYYY-MM-DD, or null when speedrun.com has none. */
  date: string | null;
  /** The run on speedrun.com. */
  link: string | null;
  /** An individual level or time trial rather than a full game. */
  level: boolean;
}

/** What the notice board's speedrun slip shows. */
export interface SpeedrunNote {
  /** "live" from speedrun.com, "set" by hand. */
  source: "live" | "set";
  /** The game, or the hand-set title. */
  title: string;
  /** The category ("Glitchless"); null on a hand-set note. */
  category: string | null;
  /** The clock: h:mm:ss, or m:ss (with .cc when the time has hundredths). */
  time: string;
  place: number | null;
  date: string | null;
  link: string | null;
}

/** The household's hand-set override (speedrun_notes): a title and a time, or neither (live), and whether it is hidden. */
export interface SpeedrunSetting {
  title: string | null;
  time_s: number | null;
  hidden: boolean;
}

/** GET /api/speedrun: what shows, the live PB under it, the hand-set values, and whether this caller may change them. */
export interface SpeedrunState {
  note: SpeedrunNote | null;
  live: SpeedrunNote | null;
  set: { title: string; time: string } | null;
  hidden: boolean;
  may_edit: boolean;
}

/** PUT /api/speedrun's body: a hand-set title and time ("5:55:31"). */
export interface SetSpeedrunBody {
  title: string;
  time: string;
}

/** Who may change the note: Hue himself (his name as notes match names, shared/notes.ts sameName), or a steward (the workshop's Dev tools). */
export function canEditSpeedrun(caller: { name: string; role: string }): boolean {
  return caller.role === "steward" || (caller.name.trim().length > 0 && sameName(caller.name, SPEEDRUN_RUNNER));
}

/** The refusal for anyone else. */
export const SPEEDRUN_REFUSAL = "Only Hue can change the speedrun note.";

type Raw = Record<string, unknown>;
const obj = (v: unknown): Raw | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : null);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim().length > 0 ? v.trim() : null);

/** One entry of speedrun.com's personal-bests answer (with embed=game,category), or null when it lacks what the slip needs or is not a TTYD run (SPEEDRUN_GAME_ID). */
function pbOf(entry: unknown): (SpeedrunPb & { order: string }) | null {
  const e = obj(entry);
  const run = obj(e?.run);
  if (!e || !run) return null;
  const gameData = obj(obj(e.game)?.data);
  const gameId = str(gameData?.id) ?? str(run.game);
  if (gameId !== SPEEDRUN_GAME_ID) return null;
  const times = obj(run.times);
  const time_s = typeof times?.primary_t === "number" && Number.isFinite(times.primary_t) && times.primary_t > 0 ? times.primary_t : null;
  const game = str(obj(gameData?.names)?.international);
  const category = str(obj(obj(e.category)?.data)?.name);
  if (time_s === null || !game || !category) return null;
  const date = typeof run.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(run.date) ? run.date : null;
  const submitted = str(run.submitted);
  const link = str(run.weblink);
  return {
    game,
    category,
    time_s,
    place: typeof e.place === "number" && Number.isInteger(e.place) && e.place > 0 ? e.place : null,
    date,
    link: link && /^https:\/\/(www\.)?speedrun\.com\//.test(link) ? link : null,
    level: run.level !== null && run.level !== undefined,
    // Newest by when it went up (owner, 2026-10-23: "It should be most recently posted"); the run's
    // day only when speedrun.com has no submitted time. ISO strings sort as they read.
    order: submitted ?? date ?? "",
  };
}

/**
 * The PB the slip shows: Hue's most recently posted Paper Mario: The Thousand-Year Door full-game
 * run (no level); only when there is none, the most recently posted TTYD individual level. Runs of
 * any other game never count. Null when the answer has no TTYD run it can use.
 */
export function pickLatestPb(answer: unknown): SpeedrunPb | null {
  const data = obj(answer)?.data;
  if (!Array.isArray(data)) return null;
  const pbs = data.map(pbOf).filter((p): p is SpeedrunPb & { order: string } => p !== null);
  const pool = pbs.some((p) => !p.level) ? pbs.filter((p) => !p.level) : pbs;
  let best: (SpeedrunPb & { order: string }) | null = null;
  for (const p of pool) if (!best || p.order > best.order) best = p;
  if (!best) return null;
  const { order: _order, ...pb } = best;
  return pb;
}

/** The user id from speedrun.com's `/users?twitch=` answer, or null. */
export function userIdOf(answer: unknown): string | null {
  const data = obj(answer)?.data;
  if (!Array.isArray(data)) return null;
  const id = str(obj(data[0])?.id);
  return id && /^[a-z0-9]+$/i.test(id) ? id : null;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * A run's time on a speedrun clock: "5:55:31" from an hour, "16:19.33" or "8:31" under one. The
 * hundredths show only when the time has them, and only under an hour; never rounded up.
 */
export function formatRunTime(seconds: number): string {
  const cs = Math.floor(Math.round(seconds * 1000) / 10);
  const whole = Math.floor(cs / 100);
  const frac = cs % 100;
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`;
  return `${m}:${pad2(s)}${frac > 0 ? `.${pad2(frac)}` : ""}`;
}

/**
 * A typed time as seconds: h:mm:ss, m:ss or m:ss.cc (one or two digits after the point; h:mm:ss.cc
 * too). Minutes and seconds under 60 after the first part; at least a second; under 100 hours.
 * Null for anything else.
 */
export function parseRunTime(text: string): number | null {
  const t = text.trim();
  const m = /^(\d{1,2})(?::(\d{2}))?:(\d{2})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const [, a, b, c, f] = m;
  const hours = b === undefined ? 0 : Number(a);
  const minutes = b === undefined ? Number(a) : Number(b);
  const secs = Number(c);
  if (b !== undefined && minutes > 59) return null;
  if (b === undefined && minutes > 59) return null;
  if (secs > 59) return null;
  const cs = f === undefined ? 0 : Number(f.padEnd(2, "0"));
  const total = hours * 3600 + minutes * 60 + secs + cs / 100;
  if (total < 1 || total > SPEEDRUN_TIME_MAX_S) return null;
  return Math.round(total * 100) / 100;
}

/** The form's line for a time that will not parse. */
export const SPEEDRUN_TIME_HINT = "Time like 5:55:31 or 8:31.25";

/** 1st, 2nd, 3rd, 4th, 11th, 12th, 13th, 21st, 112th. */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const unit = n % 10;
  return `${n}${unit === 1 ? "st" : unit === 2 ? "nd" : unit === 3 ? "rd" : "th"}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A run's day as the slip says it: "Oct 16, 2026". */
export function runDateLabel(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]} ${Number(m[3])}, ${m[1]}`;
}

/** The open slip's line under the time: "2nd place · Oct 16, 2026", either part alone, or "" for neither. */
export function speedrunMetaLine(note: Pick<SpeedrunNote, "place" | "date">): string {
  return [note.place ? `${ordinal(note.place)} place` : null, note.date ? runDateLabel(note.date) : null].filter(Boolean).join(" · ");
}

/** The slip's heading, over the title: whose best it is. */
export const SPEEDRUN_HEAD = "Hue's best";
/** The open slip's link out. */
export const SPEEDRUN_LINK_LABEL = "See the run";
/** The open slip's button for Hue and stewards, and the sheet it opens. */
export const SPEEDRUN_EDIT_LABEL = "Change";
export const SPEEDRUN_SHEET_LABEL = "Speedrun note";
export const SPEEDRUN_TITLE_LABEL = "Title";
export const SPEEDRUN_TITLE_PLACEHOLDER = "Game or run";
export const SPEEDRUN_TIME_LABEL = "Time";
export const SPEEDRUN_TIME_PLACEHOLDER = "5:55:31";
export const SPEEDRUN_SAVE_LABEL = "Save";
export const SPEEDRUN_SAVE_BUSY = "Saving…";
/** Drops the hand-set title and time: the note follows speedrun.com again. Shown only while one is set. */
export const SPEEDRUN_BACK_LABEL = "Back to live";
/** Dev tools only: the sheet's quiet word to take the note off the board, or put it back. */
export function speedrunHideLabel(hidden: boolean): string {
  return hidden ? "Put it back up" : "Take it down";
}
/** The sheet's line while nothing is known from speedrun.com yet and nothing is set. */
export const SPEEDRUN_NOTHING_YET = "No times yet. Set one by hand.";
/** The form's own check before sending: the server says the same. */
export function speedrunFormError(title: string, time: string): string | null {
  if (!title.trim()) return "Give it a title";
  if (Array.from(title.trim()).length > SPEEDRUN_TITLE_MAX) return "That title is too long";
  if (parseRunTime(time) === null) return SPEEDRUN_TIME_HINT;
  return null;
}

/** The live PB as the board shows it. */
export function liveNoteOf(pb: SpeedrunPb): SpeedrunNote {
  return { source: "live", title: pb.game, category: pb.category, time: formatRunTime(pb.time_s), place: pb.place, date: pb.date, link: pb.link };
}

/** What the board shows: hidden, nothing; a hand-set title and time win over the live PB; else the live PB, or nothing yet. */
export function speedrunNoteOf(setting: SpeedrunSetting | null, live: SpeedrunPb | null): SpeedrunNote | null {
  if (setting?.hidden) return null;
  if (setting && setting.title && setting.time_s !== null) {
    return { source: "set", title: setting.title, category: null, time: formatRunTime(setting.time_s), place: null, date: null, link: null };
  }
  return live ? liveNoteOf(live) : null;
}

/**
 * The time's highlight on the slip, closed and open, live or hand-set (owner, 2026-10-20: "The time
 * should be highlighted with a non user tagging color, something muted"): a name tag's pill (pale
 * solid fill, the tag's ink, a two-pixel underline) in a dusty khaki no peon can ever wear. It is no
 * shirt and no fallback colour, at least 8 apart in Lab from every tag a household could get, and
 * the ink reads on it at 7:1 or better (tests/mentions.test.ts holds all three).
 */
export const SPEEDRUN_TIME_COLOUR: MentionColour = { swatch: "#867a58", tint: "#d0c8a8", ink: MENTION_INK };

/** A screen reader's name for the closed slip: "Hue's best: Paper Mario: The Thousand-Year Door, 5:55:31". */
export function speedrunSlipLabel(note: SpeedrunNote): string {
  return `${SPEEDRUN_HEAD}: ${note.title}, ${note.time}`;
}

/**
 * The speedrun slip's stopwatch stamp (owner, 2026-10-26: "the clock icon on the speedrun card looks
 * bad"): drawn on the peon stamp's own grid (shared/weekly.ts GALLERY_PEON, 46x42 art pixels at two
 * thirds of a CSS pixel or point, two device pixels on a 3x phone) and worn in the peon stamp's frame
 * (shared/peon-stamp.ts PEON_STAMP), so the speedrun slip's corner is the same size as a note's.
 * Both clients draw it as rects on the frame's parchment, like the tally marks (scripts/preview-watch.mjs
 * renders it at 8x for judging).
 *
 * "O" ink; the case in the medals' silver, "L" its light (upper right), "M" the metal, "D" its shade
 * (lower left); the crown and the side button in the pin's golds, "Y" light, "G" gold, "B" shade;
 * "W" the face, "w" the face where the bezel's lip shades it; "s" the step shadow on the paper.
 */
export const SPEEDRUN_WATCH_SIZE = { w: GALLERY_PEON.w, h: GALLERY_PEON.h, scale: GALLERY_PEON.scale } as const;

export const SPEEDRUN_WATCH: readonly string[] = [
  "..................OOOOOOOO....................",
  ".................OYYYYYYYGO...................",
  ".................OGGGGGGGBOs..................",
  "..................OOOOOOOOsss.....OOO.........",
  "...................sOYGOsssss....OYYGO........",
  "....................OGBOssss....OYGGGBO.......",
  "..................OOOGBOOO......OGGGBBOs......",
  "...............OOOOOOOOOOOOOO....OGBBOsss.....",
  ".............OOOOOLLLLLLLLOOOOO.OGBOOssss.....",
  "............OOOMMMLLLLLLLLLLLOOOGBOsssss......",
  "..........OOOMMMMMMMMMMMMLLLLLLOOOsssss.......",
  ".........OOOMMMMMMMOOOOOOLLLLLLLOOOss.........",
  ".........OODMMMMOOOwwOOwwOOOLLLLLOOs..........",
  "........OODMMMMOwwwwWOOWWwwwOLLLLLOOs.........",
  ".......OODMMMMOwwWWWWOOWWWWWwOLLLLLOO.........",
  ".......OODMMMOwwWWWWWWWWWWWWWWOLLLLOOs........",
  "......OODDMMOwwWWWWWWWWWWOOWWWWOLLLLOOs.......",
  "......OODDMOwwWWWWWWWWWWOOWWWWWWOLLLOOs.......",
  "......OODDMOwWWWWWWWWWWWOOWWWWWWOLLLOOss......",
  ".....OODDMMOwWWWWWWWWWWOOWWWWWWWOLLLLOOs......",
  ".....OODDDOwwWWWWWWWWWOOOWWWWWWWWOMLLOOs......",
  ".....OODDDOwWWWWWWWWWOOOWWWWWWWWWOMLLOOss.....",
  ".....OODDDOOOOWWWWWWOYGOWWWWWWOOOOMLLOOss.....",
  ".....OODDDOOOOWWWWWWOGBOWWWWWWOOOOMLLOOss.....",
  ".....OODDDOwWWWWWWWWWOOWWWWWWWWWWOMLLOOss.....",
  ".....OODDDOwWWWWWWWWWWWWWWWWWWWWWOMLLOOss.....",
  ".....OODDDDOwWWWWWWWWWWWWWWWWWWWOMMLLOOss.....",
  "......OODDDOwWWWWWWWWWWWWWWWWWWWOMMMOOsss.....",
  "......OODDDOwWWWWWWWWWWWWWWWWWWWOMMMOOsss.....",
  "......OODDDDOwWWWWWWWWWWWWWWWWWOMMMMOOss......",
  ".......OODDDDOWWWWWWWWWWWWWWWWOMMMMOOsss......",
  ".......OODDDDDOWWWWWWOOWWWWWWOMMMMMOOsss......",
  "........OODDDDDOWWWWWOOWWWWWOMMMMMOOsss.......",
  ".........OODDDDDOOOWWOOWWOOOMMMMMOOssss.......",
  ".........OOODDDDDDDOOOOOOMMMMMMMOOOsss........",
  "..........OOODDDDDDMMMMMMMMMMMMOOOsss.........",
  "...........sOOODDDDDDDDDDDMMMOOOsssss.........",
  "............sOOOOODDDDDDDDOOOOOsssss..........",
  "..............sOOOOOOOOOOOOOOsssss............",
  "...............sssOOOOOOOOsssssss.............",
  ".................ssssssssssssss...............",
  "....................ssssssss..................",
];

/** The stopwatch's palette: the slip's ink, the medals' silver, the pin's golds, the parchment, the dog-ear's underside, the pin's shadow. */
export const WATCH_FILL: Readonly<Record<string, string>> = {
  O: "#2b1a0c",
  L: "#ffffff",
  M: "#b8c0cc",
  D: "#7e8794",
  Y: "#ffe58a",
  G: "#d9a21b",
  B: "#7a5a0e",
  W: "#f4e8c8",
  w: "#e0cfa8",
  s: "#b9a37c",
};

/** The stopwatch's rects in art pixels: each row's runs of one colour merged, so a client draws a few hundred rects, not two thousand. */
export function speedrunWatchPixels(): { x: number; y: number; w: number; fill: string }[] {
  const out: { x: number; y: number; w: number; fill: string }[] = [];
  SPEEDRUN_WATCH.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const ch = row[x]!;
      let end = x + 1;
      while (end < row.length && row[end] === ch) end++;
      const fill = WATCH_FILL[ch];
      if (fill) out.push({ x, y, w: end - x, fill });
      x = end;
    }
  });
  return out;
}
